import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import okReservaLogo from '../assets/okreserva-logo.png';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useAppStore } from '../store/useStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Delete, Lock, Building2, ArrowLeft, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { theme } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('restaurant'); // 'restaurant', 'pin', 'set-pin'
  const [restaurantName, setRestaurantName] = useState('');
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [error, setError] = useState('');
  const pinInputRef = useRef(null);

  useEffect(() => {
    if (step === 'pin' && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [step]);

  const handleRestaurantSubmit = async (e) => {
    e.preventDefault();
    if (!restaurantName.trim()) {
      setError('Ingresa el nombre del restaurante');
      return;
    }
    setError('');
    setStep('pin');
  };

  const handlePinKeyPress = (digit) => {
    if (pin.length < 4) {
      const newValue = pin + digit;
      setPin(newValue);
      if (newValue.length === 4) {
        submitPin(newValue);
      }
    }
  };

  const submitPin = async (pinValue) => {
    if (pinValue.length !== 4) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_name: restaurantName,
          pin: pinValue
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Error al iniciar sesión');
        setPin('');
        return;
      }
      
      if (data.requires_pin_setup) {
        setTempToken(data.access_token);
        setTempUser(data.user);
        setStep('set-pin');
        toast.info('Es tu primer acceso. Establece tu PIN de 4 dígitos.');
      } else {
        login(data.user, data.access_token);
        toast.success('Bienvenido de vuelta');
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Error de conexión');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 4 || confirmPin.length !== 4) {
      setError('El PIN debe ser de 4 dígitos');
      return;
    }
    if (newPin !== confirmPin) {
      setError('Los PINs no coinciden');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/set-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({
          pin: newPin,
          confirm_pin: confirmPin
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || 'Error al establecer PIN');
        return;
      }
      
      login(tempUser, tempToken);
      toast.success('PIN establecido exitosamente');
      navigate('/dashboard');
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const PinKeypad = ({ value, disabled }) => {
    const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'];
    
    return (
      <div className="space-y-6">
        {/* PIN Display */}
        <div className="flex justify-center gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-14 h-14 rounded-2xl border-2 flex items-center justify-center text-2xl font-bold transition-all duration-200 ${
                value.length > i 
                  ? 'border-primary bg-primary/10 text-primary scale-105' 
                  : 'border-muted-foreground/20 bg-muted/50'
              }`}
              data-testid={`pin-digit-${i}`}
            >
              {value.length > i ? '•' : ''}
            </div>
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 max-w-[300px] mx-auto">
          {digits.map((digit, index) => {
            if (digit === '') return <div key={index} />;
            if (digit === 'del') {
              return (
                <Button
                  key={index}
                  variant="ghost"
                  className="h-16 text-xl rounded-2xl hover:bg-destructive/10 hover:text-destructive"
                  onClick={handlePinDelete}
                  disabled={disabled || value.length === 0}
                  data-testid="pin-delete-btn"
                >
                  <Delete className="h-6 w-6" />
                </Button>
              );
            }
            return (
              <Button
                key={index}
                variant="outline"
                className="h-16 text-2xl font-semibold rounded-2xl hover:bg-primary/10 hover:border-primary hover:text-primary transition-all active:scale-95"
                onClick={() => handlePinKeyPress(digit)}
                disabled={disabled || value.length >= 4}
                data-testid={`pin-key-${digit}`}
              >
                {digit}
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="mx-auto mb-6">
            <img 
              src={okReservaLogo}
              alt="OkReserva" 
              className="h-14 w-auto mx-auto"
              style={{ filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'none' }}
            />
          </div>
          
          <CardTitle className="text-2xl font-bold">
            {step === 'restaurant' && 'Iniciar Sesión'}
            {step === 'pin' && restaurantName}
            {step === 'set-pin' && 'Crear tu PIN'}
          </CardTitle>
          <CardDescription className="text-base">
            {step === 'restaurant' && 'Ingresa el nombre de tu restaurante'}
            {step === 'pin' && 'Ingresa tu PIN de 4 dígitos'}
            {step === 'set-pin' && 'Establece un PIN seguro para acceder'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Restaurant Name Step */}
          {step === 'restaurant' && (
            <form onSubmit={handleRestaurantSubmit} className="space-y-6">
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="restaurant"
                  type="text"
                  placeholder="Nombre del restaurante"
                  value={restaurantName}
                  onChange={(e) => {
                    setRestaurantName(e.target.value);
                    setError('');
                  }}
                  autoFocus
                  className="pl-12 h-14 text-lg rounded-xl bg-muted/50 border-0 focus:ring-2 focus:ring-primary"
                  data-testid="restaurant-name-input"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive text-center" data-testid="login-error">
                  {error}
                </p>
              )}
              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-semibold rounded-xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25" 
                disabled={!restaurantName.trim()}
                data-testid="continue-btn"
              >
                Continuar
              </Button>
            </form>
          )}

          {/* PIN Entry Step */}
          {step === 'pin' && (
            <div className="space-y-6">
              <PinKeypad 
                value={pin} 
                disabled={loading}
              />
              {error && (
                <p className="text-sm text-destructive text-center font-medium" data-testid="pin-error">
                  {error}
                </p>
              )}
              {loading && (
                <div className="flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
              <Button 
                variant="ghost" 
                className="w-full mt-4 rounded-xl"
                onClick={() => {
                  setStep('restaurant');
                  setPin('');
                  setError('');
                }}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cambiar restaurante
              </Button>
            </div>
          )}

          {/* Set PIN Step (First Login) */}
          {step === 'set-pin' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nuevo PIN</label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="••••"
                  value={newPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setNewPin(val);
                    setError('');
                  }}
                  className="text-center text-3xl tracking-[1em] font-mono h-16 rounded-xl bg-muted/50 border-0"
                  autoFocus
                  data-testid="new-pin-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirmar PIN</label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="••••"
                  value={confirmPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setConfirmPin(val);
                    setError('');
                  }}
                  className="text-center text-3xl tracking-[1em] font-mono h-16 rounded-xl bg-muted/50 border-0"
                  data-testid="confirm-pin-input"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive text-center font-medium" data-testid="set-pin-error">
                  {error}
                </p>
              )}
              <Button 
                className="w-full h-14 text-lg font-semibold rounded-xl shadow-lg shadow-primary/25" 
                onClick={handleSetPin}
                disabled={loading || newPin.length !== 4 || confirmPin.length !== 4}
                data-testid="set-pin-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                Establecer PIN
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Super Poderes Button - Exactly below the card */}
      <Link to="/superpoderes" className="mt-4">
        <Button 
          variant="ghost" 
          className="text-muted-foreground hover:text-foreground gap-2 group"
          data-testid="superpoderes-btn"
        >
          <Sparkles className="h-4 w-4 group-hover:text-orange-500 transition-colors" />
          <span>Super Poderes de OkReserva</span>
        </Button>
      </Link>
      
      {/* Footer */}
      <div className="fixed bottom-4 text-center text-sm text-muted-foreground">
        Powered by <span className="font-semibold text-gradient-orange">OkReserva</span>
        {' · '}
        <Link 
          to="/clientes" 
          className="font-semibold text-orange-400 hover:text-orange-300 transition-colors"
          onClick={(e) => {
            // Clear any stored client session to ensure clean login
            localStorage.removeItem('client_token');
            localStorage.removeItem('client_user');
          }}
        >
          OUser
        </Link>
        {' '}
        <button
          onClick={() => {
            // Clear all client-related cache
            localStorage.removeItem('client_token');
            localStorage.removeItem('client_user');
            sessionStorage.clear();
            toast.success('Cache limpiado');
            window.location.href = '/clientes';
          }}
          className="text-xs text-gray-400 hover:text-orange-400 transition-colors ml-1"
          title="Limpiar cache del cliente"
        >
          (cache)
        </button>
      </div>
    </div>
  );
}
